import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  updateIdentityField,
  setCurrentStep,
  setNextStep,
  setIdentityType,
  syncStepWithServer,
  UnifiedFormState,
} from '../Slice/unifiedFormSlice';

interface IdentityData {
  id: number;
  name: string;
  display_name: string;
  slug?: string;
  status: string;
  active: boolean;
  content_status: string;
  identity_type: 'LEGAL' | 'PERSONAL';
  step: number;
  next_step?: number;
  ['next-step']?: number;
}

interface UpdateIdentityDataReturn {
  updateIdentityData: (data: IdentityData) => void;
  error: string | null;
}

export const useUpdateIdentityData = (): UpdateIdentityDataReturn => {
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);

  const updateIdentityData = useCallback(
    (data: IdentityData) => {
      try {
        console.log('Updating identity data:', data);

        // Extract next step from both possible field names
        const serverNextStep = data.next_step ?? data['next-step'];
        console.log('Extracted next step:', serverNextStep);

        // Define valid identity fields to update
        const identityUpdates: Partial<UnifiedFormState['identity']> = {
          id: data.id,
          name: data.display_name,
          display_name: data.name,
          status: data.status,
          active: data.active ? 1 : 0,
          content_status: data.content_status,
          step: data.step,
        };

        // Note: 'slug' is not included because it's not defined in UnifiedFormState['identity'].
        // If you need to include 'slug', add it to the UnifiedFormState['identity'] interface in unifiedFormSlice.ts:
        // slug?: string;

        // Dispatch identity updates for defined fields
        (Object.keys(identityUpdates) as Array<keyof UnifiedFormState['identity']>).forEach((field) => {
          const value = identityUpdates[field];
          if (value !== undefined) {
            dispatch(
              updateIdentityField({
                field,
                value,
              }),
            );
          }
        });

        // Update identity type
        dispatch(setIdentityType(data.identity_type));

        // Update current step
        dispatch(setCurrentStep(data.step));

        // Update next step if provided, otherwise increment current step
        if (serverNextStep !== undefined && serverNextStep !== null) {
          console.log(`Dispatching setNextStep: ${serverNextStep}`);
          dispatch(setNextStep(serverNextStep));
        } else {
          console.warn('No valid next step provided, incrementing step');
          dispatch(setNextStep(data.step + 1));
        }

        // Sync step with server
        dispatch(syncStepWithServer({ currentStep: data.step, nextStep: serverNextStep ?? data.step + 1 }));

        setError(null);
      } catch (err: any) {
        console.error('Error updating identity data:', err);
        setError(err.message || 'خطا در به‌روزرسانی اطلاعات هویت');
      }
    },
    [dispatch],
  );

  return { updateIdentityData, error };
};